/**
 * Overlay store - tracks full-screen overlays that cover the app root (e.g. the
 * challenge SuccessModal).
 *
 * Root-level XP feedback plays on a fixed timeline and self-dismisses: the "+X XP"
 * flash (XpGainHost) and the level-up / rank-up ceremonies (CelebrationHost). A
 * cover mounted over them burns that timeline unseen - creating a challenge spends
 * stake coins, which refetches XP and fires the flash while SuccessModal is still up.
 * Those hosts defer while `blockingCount > 0` and play once the last cover closes.
 *
 * A counter, not a boolean, so overlapping overlays can't unblock each other early.
 */

import { create } from 'zustand';

interface OverlayStoreState {
  blockingCount: number;
  pushBlockingOverlay: () => void;
  popBlockingOverlay: () => void;
}

export const useOverlayStore = create<OverlayStoreState>((set) => ({
  blockingCount: 0,
  pushBlockingOverlay: () => set((s) => ({ blockingCount: s.blockingCount + 1 })),
  // Floored at 0: an unmatched pop must not leave the count negative and wedge the gate.
  popBlockingOverlay: () => set((s) => ({ blockingCount: Math.max(0, s.blockingCount - 1) })),
}));

/** True while a full-screen overlay covers the app root. Root-level feedback should wait. */
export const useIsBlockedByOverlay = (): boolean => useOverlayStore((s) => s.blockingCount > 0);
